<template>
  <div class="flex items-center bg-white py-3 px-5 cursor-pointer" @click="toggleExpanded">
    <div class="w-20 flex-shrink-0">
      <div class="flex items-center gap-2">
        <span
          class="inline-flex min-w-6 h-6 px-1.5 items-center justify-center rounded bg-slate-100 text-blue-800 text-xs font-medium"
        >
          {{ searchNumber }}
        </span>
        <div
          v-if="!isSearchTimeRangeValid"
          v-tooltip="getInvalidTimeRangeWarningMessage()"
          class="text-yellow-600 relative"
        >
          <div
            class="absolute animate-ping bg-yellow-600 size-full rounded-full top-0 left-0 opacity-40"
          />
          <ExclamationIcon class="size-4" />
        </div>
      </div>
    </div>
    <div class="w-44 flex-shrink-0">
      <span class="text-slate-900 text-sm">
        {{
          search.searchDate ? format(new Date(search.searchDate), settings.dateFormat.value) : "-"
        }}
      </span>
    </div>
    <div
      v-if="importSource.id === BuiltInImportSourceId.CITATION_SEARCH"
      class="w-20 flex-shrink-0"
    >
      <span
        v-tooltip="'Title: ' + (getStudyById(search.parentStudyId!) as Article)?.metadata?.title"
        class="flex items-center gap-1 text-slate-900 text-sm"
      >
        {{ search.parentStudyId ?? "-" }}
        <InformationCircleIcon class="text-slate-400 w-4 h-4" />
      </span>
    </div>
    <div
      v-if="
        importSource.id !== BuiltInImportSourceId.HAND_SEARCH &&
        importSource.id !== BuiltInImportSourceId.CITATION_SEARCH
      "
      class="flex-1 min-w-0"
    >
      <div class="flex items-center gap-1">
        <div class="text-slate-900 p-0.5 flex-shrink-0">
          <ChevronDownIcon v-if="isExpanded" class="w-4 h-4 ring-1 ring-slate-900 rounded-full" />
          <ChevronRightIcon v-else class="w-4 h-4 ring-1 ring-slate-900 rounded-full" />
        </div>
        <span class="text-slate-900 text-sm truncate">
          Search:
          {{ search.searchQuery ? truncateText(search.searchQuery, 40) : "-" }}
        </span>
      </div>
    </div>
    <div v-else class="flex-1 min-w-0"></div>
    <div class="flex items-center flex-shrink-0">
      <div class="w-20 flex justify-center">
        <a
          v-if="getSearchUrl(search)"
          :href="getSearchUrl(search)"
          target="_blank"
          rel="noopener"
          class="inline-flex min-w-6 h-6 px-1.5 items-center justify-center rounded bg-slate-100 text-blue-800 text-xs font-medium hover:underline"
        >
          {{ search.studies?.length }}
        </a>
        <span
          v-else
          class="inline-flex min-w-6 h-6 px-1.5 items-center justify-center rounded bg-slate-100 text-blue-800 text-xs font-medium"
        >
          {{ search.studies?.length }}
        </span>
      </div>
      <div class="w-16 flex justify-end">
        <PlanLockGuard>
          <FloatingMenu>
            <template #activator="{ show }">
              <button
                class="p-1"
                :class="{
                  'text-gray-300': review.isPlanReadonly.value,
                  'text-red-500 hover:text-red-600 ': !review.isPlanReadonly.value,
                }"
                :disabled="review.isPlanReadonly.value"
                @click="show"
              >
                <TrashIcon class="w-4 h-4" />
              </button>
            </template>
            <template #content="{ hide }">
              <div class="p-4 space-y-2">
                <div>
                  <p v-if="doesSearchContainScreenedStudies(search)">
                    This search contains screened studies, are you sure you want to delete it?
                  </p>
                  <p v-else>Are you sure you want to delete this search?</p>
                </div>
                <div>
                  <button
                    class="bg-neutral-500 hover:bg-neutral-600 text-white py-1 px-2 mr-1"
                    @click.stop="hide"
                  >
                    Cancel
                  </button>
                  <button
                    class="bg-red-500 hover:bg-red-600 text-white py-1 px-2"
                    @click.stop="
                      () => {
                        removeSearch(search).then(() => hide());
                      }
                    "
                  >
                    Delete
                  </button>
                </div>
              </div>
            </template>
          </FloatingMenu>
        </PlanLockGuard>
      </div>
    </div>
  </div>
  <div
    v-if="
      isExpanded &&
      importSource.id !== BuiltInImportSourceId.HAND_SEARCH &&
      importSource.id !== BuiltInImportSourceId.CITATION_SEARCH
    "
    class="flex bg-white"
  >
    <div class="flex-1 min-w-0 py-4 border-t border-slate-200">
      <div class="space-y-2 text-sm pl-5">
        <p>
          <span class="font-medium text-slate-900">Search: </span>
          <span class="text-slate-700">{{ search.searchQuery ? search.searchQuery : "-" }}</span>
        </p>
        <p
          v-if="
            importSource.id !== BuiltInImportSourceId.EMBASE ||
            (search.filters && importSource.id === BuiltInImportSourceId.EMBASE)
          "
        >
          <span class="font-medium text-slate-900">Filters: </span>
          <span class="text-slate-700">{{
            search.filters
              ? importSource.id === BuiltInImportSourceId.PMC
                ? search.filters
                : formatDatesInSearchFilters(search.filters)
              : "-"
          }}</span>
        </p>
        <p
          v-if="
            importSource.id === BuiltInImportSourceId.PUBMED ||
            importSource.id === BuiltInImportSourceId.PMC
          "
        >
          <span class="font-medium text-slate-900">Search details: </span>
          <span class="text-slate-700 whitespace-pre-wrap"
            >{{
              search.queryTranslation ? updateEndDateInPubmedQuery(search.queryTranslation) : "-"
            }}
          </span>
        </p>
        <div v-if="importSource.id === BuiltInImportSourceId.EMBASE && search.queryTranslation">
          <p class="font-medium text-slate-900 mb-1">Search details:</p>
          <div
            v-for="(entry, i) in parsedEmbaseHistory"
            :key="i"
            class="flex items-start gap-1 py-0.5"
          >
            <span class="text-slate-500 flex-shrink-0 w-8">{{ entry.label }}:</span>
            <span class="flex-1 text-slate-700 break-words">{{ entry.query }}</span>
            <span class="w-20 flex-shrink-0 flex justify-center">
              <span
                class="inline-flex min-w-6 h-6 px-1.5 items-center justify-center rounded-full bg-blue-100 text-blue-800 text-xs font-medium"
              >
                {{ entry.hits ?? "-" }}
              </span>
            </span>
            <span class="w-16 flex-shrink-0" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { Search } from "@core/domain/models/search.model";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import { InformationCircleIcon } from "@heroicons/vue/24/outline";
import { updateEndDateInPubmedQuery } from "@app/utils/updateEndDateInPubmedQuery";
import { Article } from "@core/domain/models/article.model";
import { format, parse, isValid } from "date-fns";
import { useSettings } from "@app/composables/use-settings";
import useLoading from "@app/composables/use-loading";
import useSnackbar from "@app/composables/use-snackbar";
import { SnackbarState } from "@app/types";
import { HttpException } from "@core/exceptions/http.exception";
import { errorMessage } from "@app/utils/error-message";
import { ReviewKey } from "@app/views/Review/use-review";
import { injectStrict } from "@app/utils/injectStrict";
import { BuiltInImportSourceId } from "@core/domain/types/builtInImportSourceId";
import { ImportSource } from "@core/domain/models/import-source.model";
import TrashIcon from "@app/components/Icons/TrashIcon.vue";
import PlanLockGuard from "@app/views/Review/Plan/PlanLockGuard.vue";
import { computed, ref } from "vue";
import ExclamationIcon from "@app/components/Icons/ExclamationIcon.vue";
import ChevronDownIcon from "@app/components/Icons/ChevronDownIcon.vue";
import ChevronRightIcon from "@app/components/Icons/ChevronRightIcon.vue";
import { extractSearchDates, normalizeDate, parseDate } from "@app/utils/extractSearchDates";

const props = defineProps<{
  search: Search;
  importSource: ImportSource;
  searchNumber: number;
}>();

const loading = useLoading();
const snackbar = useSnackbar();

const isExpanded = ref(false);

function toggleExpanded() {
  isExpanded.value = !isExpanded.value;
}

const parsedEmbaseHistory = computed(() => {
  if (!props.search.queryTranslation) return [];
  return props.search.queryTranslation.split("\n").map((line) => {
    const trimmed = line.trim();
    const match = trimmed.match(/^(#\d+):\s+(.*?)\s+\((\d+) results\)$/);
    if (match) return { label: match[1], query: match[2], hits: match[3] };
    return { label: "", query: trimmed, hits: null };
  });
});

const review = injectStrict(ReviewKey);
const settings = useSettings();

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
}

function getSearchUrl(search: Search) {
  return search.getSearchUrl();
}

function formatDatesInSearchFilters(searchFilters: string): string {
  const dateMatches = searchFilters.match(/\d{1,4}([/.-])\d{1,2}\1\d{2,4}/g);

  if (dateMatches) {
    const formattedDates = dateMatches.map((dateStr) => {
      let parsedDate = parse(dateStr, "dd/MM/yyyy", new Date());

      if (!isValid(parsedDate)) {
        parsedDate = parse(dateStr, "yyyy/MM/dd", new Date());
      }

      if (!isValid(parsedDate)) {
        parsedDate = parse(dateStr, "dd-MM-yyyy", new Date());
      }

      if (!isValid(parsedDate)) {
        parsedDate = parse(dateStr, "yyyy-MM-dd", new Date());
      }

      if (!isValid(parsedDate)) {
        parsedDate = parse(dateStr, "dd.MM.yyyy", new Date());
      }

      if (!isValid(parsedDate)) {
        parsedDate = parse(dateStr, "yyyy.MM.dd", new Date());
      }

      if (isValid(parsedDate)) {
        return {
          original: dateStr,
          formatted: format(parsedDate, settings.dateFormat.value),
        };
      } else {
        return {
          original: dateStr,
          formatted: dateStr,
        };
      }
    });

    return formattedDates.reduce(
      (newText, { original, formatted }) => newText.replace(original, formatted),
      searchFilters,
    );
  }

  return searchFilters;
}

async function removeSearch(search: Search) {
  if (!search) throw new Error("no search selected");
  try {
    loading.start();
    await review.removeSearch(search.id!);
    snackbar.success("Success");
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
  } finally {
    loading.stop();
  }
}

function doesSearchContainScreenedStudies(search: Search) {
  return search.studies?.some((s) => s.titleAndAbstractScreening ?? s.fullTextScreening);
}

function getStudyById(id: number) {
  const study = review.entity.value.studies?.find((s) => s.id === id);
  return study;
}

const isSearchTimeRangeValid = computed(() => {
  const studyDesignTimeLimitFromDate =
    review.entity.value.plan?.inclusionCriteria.timeLimitFromDate;
  const studyDesignTimeLimitToDate = review.entity.value.plan?.inclusionCriteria.timeLimitToDate;

  const dateRange = extractSearchDates(
    props.search.searchQuery ?? null,
    props.search.filters ?? null,
  );

  if (dateRange && dateRange.startDate && dateRange.endDate) {
    const parsedStartDate = parseDate(dateRange.startDate);
    const parsedEndDate = parseDate(dateRange.endDate);

    if (parsedStartDate && parsedEndDate) {
      if (studyDesignTimeLimitFromDate) {
        const limitFromDate = new Date(studyDesignTimeLimitFromDate);
        if (normalizeDate(parsedStartDate) < normalizeDate(limitFromDate)) {
          return false;
        }
      }
      if (studyDesignTimeLimitToDate) {
        const limitToDate = new Date(studyDesignTimeLimitToDate);
        if (normalizeDate(parsedEndDate) > normalizeDate(limitToDate)) {
          return false;
        }
      }
    }
  }
  return true;
});

function getInvalidTimeRangeWarningMessage(): string {
  const studyDesignTimeLimitFromDate =
    review.entity.value.plan?.inclusionCriteria.timeLimitFromDate;
  const studyDesignTimeLimitToDate = review.entity.value.plan?.inclusionCriteria.timeLimitToDate;

  if (studyDesignTimeLimitFromDate && studyDesignTimeLimitToDate)
    return (
      "Search time range does not match plan strategy time limits: " +
      format(new Date(studyDesignTimeLimitFromDate), settings.dateFormat.value) +
      " - " +
      format(new Date(studyDesignTimeLimitToDate), settings.dateFormat.value)
    );
  return "";
}
</script>
